import { motion } from "framer-motion";
import {
  Stethoscope,
  ClipboardList,
  BrainCircuit,
  ShieldCheck,
  FileBadge,
  CalendarClock,
  ArrowUpRight,
} from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { Icon3D } from "./Icon3D";

const solutions = [
  {
    icon: Stethoscope,
    title: "Entrevista Qualificada",
    desc: "Condução por enfermeiros avaliadores, com análise da Declaração de Saúde, apuração clínica, identificação de morbidades, CID e recomendação técnica.",
    variant: "deepBlue",
    href: "/solucoes/entrevista-medica-qualificada",
  },
  {
    icon: ClipboardList,
    title: "Parecer Técnico",
    desc: "Relatórios objetivos para apoiar decisões comerciais, assistenciais e operacionais da operadora.",
    variant: "vividBlue",
    href: "/solucoes/analise-declaracao-saude",
    linkLabel: "Análise da Declaração de Saúde",
  },
  {
    icon: BrainCircuit,
    title: "Inteligência de Carteira",
    desc: "Leitura epidemiológica, estratificação de risco e dados para maior previsibilidade da carteira.",
    variant: "aqua",
  },
  {
    icon: ShieldCheck,
    title: "Rastreabilidade e Segurança",
    desc: "Videoatendimento gravado, histórico documentado, critérios padronizados e trilha de evidência.",
    variant: "vividBlue",
  },
  {
    icon: FileBadge,
    title: "Gestão de CPT",
    desc: "Apoio técnico para identificar situações de cobertura parcial temporária quando aplicável.",
    variant: "deepBlue",
    href: "/solucoes/gestao-cpt",
  },
  {
    icon: CalendarClock,
    title: "Operação Flexível",
    desc: "Atendimento por agendamento, fila em tempo real e ajuste de capacidade conforme demanda.",
    variant: "aqua",
  },
];

export const Solutions = () => {
  return (
    <section
      id="solucoes"
      data-testid="solutions-section"
      className="relative py-20 md:py-28 bg-gradient-to-br from-[#63bda5] to-[#93ccad] overflow-hidden"
    >
      <div className="blob w-[400px] h-[400px] top-20 -right-32 bg-white/20" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <SectionHeading
          overline="Soluções"
          title="Soluções para uma operação"
          highlight="mais segura e inteligente"
          subtitle="Um portfólio técnico desenhado para qualificar risco desde a adesão até a gestão contínua da carteira."
          testid="solutions-heading"
          invert
        />

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {solutions.map((s, idx) => (
            <motion.article
              key={s.title}
              data-testid={`solution-card-${idx}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.55,
                ease: [0.16, 1, 0.3, 1],
                delay: idx * 0.06,
              }}
              whileHover={{ y: -6 }}
              className="group relative bg-white rounded-3xl border border-[#E5EAF2] p-8 hover:shadow-[0_24px_60px_rgba(53,50,179,0.10)] hover:border-[#3787F5]/30 transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <Icon3D
                  icon={s.icon}
                  variant={s.variant}
                  size="lg"
                  className="group-hover:scale-110 group-hover:-rotate-3"
                />
                <ArrowUpRight
                  size={20}
                  className="text-[#CBD5E1] group-hover:text-[#3787F5] group-hover:rotate-12 transition-all duration-300"
                />
              </div>
              <h3 className="mt-7 text-xl font-bold text-[#1F2937]">
                {s.title}
              </h3>
              <p className="mt-3 text-[#64748B] leading-relaxed text-[15px]">
                {s.desc}
              </p>
              {s.href && (
                <a
                  href={s.href}
                  data-testid={`solution-link-${idx}`}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3532B3] hover:underline"
                >
                  Saiba mais sobre {s.linkLabel || s.title}
                  <ArrowUpRight size={15} />
                </a>
              )}
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
